import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { CrmLearningProfile, CrmLearningProfileAccessGate } from '@features/crm-learning-profiles';
import { firstAccessibleCrmCustomerRecordArea } from '@widgets/crm-customer-record-shell';

const certificationSearchSchema = z.object({
  certificationProfileTab: z.enum(['general', 'completion-form', 'certification-card']).optional(),
});

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/learning/certifications/$certificationId'
)({
  component: CertificationProfileRoute,
  validateSearch: certificationSearchSchema,
});

function CertificationProfileRoute() {
  const navigate = Route.useNavigate();
  const { certificationId, userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmLearningProfileAccessGate
        customerId={customerId}
        onAccessUnavailable={(allowedAreas) => {
          const fallback = firstAccessibleCrmCustomerRecordArea(allowedAreas);
          return (
            <Navigate
              params={{ userProfileId: customerId, locale }}
              replace
              to={fallback?.route ?? '/$locale/unauthorized'}
            />
          );
        }}
      >
        {(viewerAccessScope) => (
          <CrmLearningProfile
            activeTab={search.certificationProfileTab ?? 'general'}
            customerId={customerId}
            kind="certification"
            locale={locale}
            profileId={certificationId}
            viewerAccessScope={viewerAccessScope}
            onBack={() => {
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/learning',
                params: { userProfileId: customerId, locale },
                search: (current: Record<string, unknown>) => {
                  const learningSearch = { ...current };
                  delete learningSearch.certificationProfileTab;
                  return learningSearch;
                },
              });
            }}
            onTabChange={(certificationProfileTab) => {
              void navigate({
                replace: true,
                search: (current: Record<string, unknown>) => ({
                  ...current,
                  certificationProfileTab:
                    certificationProfileTab === 'general'
                      ? undefined
                      : (certificationProfileTab as 'completion-form' | 'certification-card'),
                }),
              });
            }}
          />
        )}
      </CrmLearningProfileAccessGate>
    </main>
  );
}
